import { useEffect, useState } from "react";

interface AnimatedRingProps {
  readonly value: number;
  readonly fraction?: number;
  readonly centerLabel: string;
  readonly sublabel?: string;
  readonly size?: number;
}

const COUNT_UP_MS = 1000;

/** Calorie ring that sweeps in on mount with a count-up center number. */
export function AnimatedRing({
  value,
  fraction = 1,
  centerLabel,
  sublabel,
  size = 200,
}: AnimatedRingProps) {
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [sweep, setSweep] = useState(0);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setSweep(1));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / COUNT_UP_MS);
      setDisplayValue(Math.round(value * t));
      if (t >= 1) clearInterval(timer);
    }, 32);
    return () => clearInterval(timer);
  }, [value]);

  const dashOffset = circumference * (1 - fraction * sweep);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-muted)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold tabular-nums text-text">
          {displayValue.toLocaleString()}
        </span>
        <span className="text-sm font-medium text-text-muted">{centerLabel}</span>
        {sublabel && <span className="text-xs text-text-muted">{sublabel}</span>}
      </div>
    </div>
  );
}
